import { Injectable } from '@angular/core';
import { CommonDataParamsService } from './common-data-params.service';
import { TagUtils } from './tag.utils';
import { PortfolioApiService } from '../services/customer-api/portfolio-api.service';
import {
  TopEventsRequestParams,
  BullBearTopEventsRequestParams,
} from '../models/score-events.interface';

const RISK_SCORES_WITH_BLACK_SWAN = [
  'general_risk',
  'esg_risk',
  'immediate_risk',
];

const ESG_SCORES_WITH_MATERIALITY_DISABLED = [
  'esg',
  'e_standard',
  's_standard',
  'g_standard',
];

// credit_watch requires explicit risk_sensitivity=0; the Data API behaves
// differently when it's omitted.
const CREDIT_WATCH_RISK_SENSITIVITY = 0;

export type ScoreLabTopEventsParams = { scoreType: string } & Pick<
  TopEventsRequestParams,
  'withBlackSwan' | 'disableIndustryMateriality' | 'riskSensitivity'
>;

export type TopEventsTarget =
  | { kind: 'entity'; type: string; id: string }
  | { kind: 'portfolio'; content: unknown[] };

// Caller may override date resolution either by passing an explicit range
// (`dateFrom`+`dateTo`) or a `time` string; otherwise the service falls back
// to the current URL via `CommonDataParamsService`.
export interface ScoreLabTopEventsInput {
  scoreKey: string;
  time?: string;
  dateFrom?: string;
  dateTo?: string;
  period?: number;
  ranking?: 'absolute_count' | 'relative_importance';
  nEvents?: number;
  topEventsSign?: 'neg' | 'pos' | 'all';
  lang?: string[];
  feedTypes?: string[];
}

export interface BullBearTopEventsInput {
  time?: string;
  dateFrom?: string;
  dateTo?: string;
  period?: number;
  timeHorizon?: string;
  ranking?: 'absolute_count' | 'relative_importance';
  nEvents?: number;
  lang?: string[];
  feedTypes?: string[];
}

@Injectable({
  providedIn: 'root',
})
export class TopEventsParamsService {
  // Defaults inherited from the legacy yukkapi `with_top_events` codepath.
  static readonly SCORE_PERIOD = 30;
  static readonly BB_TIME_HORIZON = '1M';

  static isBullBearScoreKey(scoreKey: string): boolean {
    return scoreKey === 'bb';
  }

  static buildScoreParams(scoreKey: string): ScoreLabTopEventsParams {
    const params: ScoreLabTopEventsParams = { scoreType: scoreKey };

    if (RISK_SCORES_WITH_BLACK_SWAN.includes(scoreKey)) {
      params.withBlackSwan = true;
    }
    if (ESG_SCORES_WITH_MATERIALITY_DISABLED.includes(scoreKey)) {
      params.disableIndustryMateriality = true;
    }
    if (scoreKey === 'credit_watch') {
      params.riskSensitivity = CREDIT_WATCH_RISK_SENSITIVITY;
    }

    return params;
  }

  constructor(
    private commonDataParams: CommonDataParamsService,
    private portfolioApi: PortfolioApiService,
  ) {}

  resolveTarget(): TopEventsTarget {
    const parsedTag = TagUtils.parse(
      this.commonDataParams.getCurrentQueryParams().tag,
    );
    if (parsedTag) {
      return { kind: 'entity', type: parsedTag.type, id: parsedTag.id };
    }
    return {
      kind: 'portfolio',
      content: this.portfolioApi.activePortfolioContent,
    };
  }

  buildScoreLabRequest(input: ScoreLabTopEventsInput): TopEventsRequestParams {
    const { dateFrom, dateTo } = this.resolveDateRange(input);
    const params: TopEventsRequestParams = {
      dateFrom,
      dateTo,
      period: input.period ?? TopEventsParamsService.SCORE_PERIOD,
      ranking: input.ranking ?? 'absolute_count',
      nEvents: input.nEvents ?? -1,
      ...TopEventsParamsService.buildScoreParams(input.scoreKey),
    };

    if (input.topEventsSign) {
      params.topEventsSign = input.topEventsSign;
    }
    if (input.lang) {
      params.lang = input.lang;
    }
    if (input.feedTypes) {
      params.feedTypes = input.feedTypes;
    }

    return params;
  }

  buildBullBearRequest(
    input: BullBearTopEventsInput,
  ): BullBearTopEventsRequestParams {
    const { dateFrom, dateTo } = this.resolveDateRange(input);
    const params: BullBearTopEventsRequestParams = {
      dateFrom,
      dateTo,
      period: input.period ?? TopEventsParamsService.SCORE_PERIOD,
      timeHorizon: input.timeHorizon ?? TopEventsParamsService.BB_TIME_HORIZON,
      ranking: input.ranking ?? 'absolute_count',
      nEvents: input.nEvents ?? -1,
    };

    if (input.lang) {
      params.lang = input.lang;
    }
    if (input.feedTypes) {
      params.feedTypes = input.feedTypes;
    }

    return params;
  }

  private resolveDateRange(input: {
    time?: string;
    dateFrom?: string;
    dateTo?: string;
  }): { dateFrom: string; dateTo: string } {
    const range = this.commonDataParams.generateTimeRange(input);
    return { dateFrom: range.date_from, dateTo: range.date_to };
  }
}
